import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { analyses as analysesApi } from '@/api/analyses';
import { dashboard as dashboardApi } from '@/api/dashboard';
import type { AnalysisListFilters, AnalysisStatus, WorkItemView } from '@/api/types';
import { useRequireOrg } from '@/tenancy/OrgContext';
import PageHeader from '@/components/PageHeader';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import EmptyState from '@/components/EmptyState';
import MultiSelect from '@/components/MultiSelect';
import WorkItemsView from '@/components/WorkItemsView';
import DateRangePicker from '@/components/DateRangePicker';
import Pagination from '@/components/Pagination';
import { useConfirm } from '@/components/ConfirmDialog';
import { useTablePageSize } from '@/lib/useTablePageSize';
import { useDateRange, applyDateRange } from '@/lib/dateRange';
import { workItemDisplayLabel } from '@/lib/workItems';
import { formatDateTime } from '@/lib/format';
import { formatEstimate } from '@/lib/estimate';
import { useEstimateMode } from '@/estimate/EstimateModeContext';

type AnalysesView = 'work-items' | 'pull-requests';

const STATUSES: AnalysisStatus[] = [
  'PENDING',
  'IN_PROGRESS',
  'COMPLETED',
  'FAILED',
  'SKIPPED_NO_CREDITS',
  'SKIPPED_BRANCH_EXCLUDED',
];

const STATUS_LABELS: Record<AnalysisStatus, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  SKIPPED_NO_CREDITS: 'Skipped — top up to analyze',
  SKIPPED_BRANCH_EXCLUDED: 'Skipped — branch not analyzed',
};

function repoDisplayName(fullName: string): string {
  const idx = fullName.indexOf('/');
  return idx === -1 ? fullName : fullName.slice(idx + 1);
}

/** Client-side title search for the work-items view (its list is already fully loaded). */
function filterWorkItems(items: WorkItemView[], term: string): WorkItemView[] {
  const t = term.trim().toLowerCase();
  if (!t) return items;
  return items.filter(wi => {
    const label = workItemDisplayLabel(wi);
    return (
      label.heading.toLowerCase().includes(t) ||
      (label.subtitle?.toLowerCase().includes(t) ?? false) ||
      wi.pullRequests.some(pr => pr.prTitle.toLowerCase().includes(t))
    );
  });
}

export default function AnalysesPage() {
  const { id: orgId, org } = useRequireOrg();
  const canDelete = org.role === 'OWNER' || org.role === 'ADMIN';
  const { mode } = useEstimateMode();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSize] = useTablePageSize('analyses', 20);

  const view: AnalysesView =
    searchParams.get('view') === 'pull-requests' ? 'pull-requests' : 'work-items';
  const page = Number(searchParams.get('page') ?? '0');
  const status = (searchParams.get('status') as AnalysisStatus | null) ?? undefined;
  const repository = searchParams.getAll('repository');
  const author = searchParams.getAll('author').map(a => a.replace(/^@/, ''));
  // Defaults to the last 30 days; "All time" is ?range=all (see useDateRange).
  const { fromDay, toDay, fromIso, toIso } = useDateRange(searchParams);

  // Title search. Kept in local state and debounced: the PR view hits the backend, the
  // work-items view filters its already-loaded list instantly off `searchInput`.
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filters: AnalysisListFilters = useMemo(
    () => ({
      page,
      size: pageSize,
      status,
      repository: repository.length ? repository : undefined,
      author: author.length ? author : undefined,
      q: debouncedSearch || undefined,
      from: fromIso,
      to: toIso,
    }),
    [page, pageSize, status, repository.join('|'), author.join('|'), debouncedSearch, fromIso, toIso],
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['analyses', orgId, filters],
    queryFn: () => analysesApi.list(orgId, filters),
    enabled: view === 'pull-requests',
  });

  const { data: filterOptions } = useQuery({
    queryKey: ['analyses-filters', orgId],
    queryFn: () => analysesApi.filters(orgId),
  });

  const workItemsQuery = useQuery({
    queryKey: ['analyses-work-items', orgId, { repository, author, fromIso, toIso }],
    // Same backend endpoint as the dashboard work-items view. Status doesn't apply there
    // (it's COMPLETED-only by definition); repo/author/date pass through.
    queryFn: () => dashboardApi.workItems(orgId, {
      repository: repository.length ? repository : undefined,
      author: author.length ? author : undefined,
      from: fromIso,
      to: toIso,
    }),
    enabled: view === 'work-items',
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => analysesApi.remove(orgId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyses', orgId] });
      qc.invalidateQueries({ queryKey: ['dashboard', orgId] });
      qc.invalidateQueries({ queryKey: ['analyses-filters', orgId] });
    },
  });

  const handleDelete = async (id: string, prNumber: number) => {
    const ok = await confirm({
      title: 'Delete analysis',
      message: `Delete analysis for PR #${prNumber} from history?\n\nThe analysis will be hidden from lists and metrics. Credits will NOT be refunded.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (ok) removeMutation.mutate(id);
  };

  const setView = (next: AnalysesView) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'work-items') params.delete('view');
    else params.set('view', next);
    params.set('page', '0');
    setSearchParams(params);
  };

  const updateMulti = useCallback(
    (key: 'repository' | 'author', values: string[]) => {
      const next = new URLSearchParams(searchParams);
      next.delete(key);
      for (const v of values) next.append(key, v);
      next.set('page', '0');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setDateRange = (from: string | null, to: string | null) => {
    const next = new URLSearchParams(searchParams);
    applyDateRange(next, from, to);
    next.set('page', '0');
    setSearchParams(next);
  };

  const setStatus = (value: AnalysisStatus | '') => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('status', value);
    else next.delete('status');
    next.set('page', '0');
    setSearchParams(next);
  };

  const setPage = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(newPage));
    setSearchParams(next);
  };

  const onPageSizeChange = (n: number) => {
    setPageSize(n);
    const next = new URLSearchParams(searchParams);
    next.set('page', '0');
    setSearchParams(next);
  };

  const repoOptions = useMemo(
    () =>
      (filterOptions?.repositories ?? []).map(full => ({
        value: full,
        label: repoDisplayName(full),
      })),
    [filterOptions],
  );

  const authorOptions = useMemo(
    () =>
      (filterOptions?.authors ?? []).map(a => ({ value: a, label: a })),
    [filterOptions],
  );

  const subtitle =
    view === 'work-items'
      ? workItemsQuery.data
        ? `${workItemsQuery.data.length} work item${workItemsQuery.data.length === 1 ? '' : 's'}`
        : undefined
      : data
        ? `${data.totalElements} pull request${data.totalElements === 1 ? '' : 's'}`
        : undefined;

  return (
    <>
      <PageHeader
        title="Analyses"
        subtitle={subtitle}
        actions={
          <div className="segmented" role="tablist" aria-label="View">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'work-items'}
              className={`segmented-btn${view === 'work-items' ? ' is-active' : ''}`}
              onClick={() => setView('work-items')}
            >
              Work items
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'pull-requests'}
              className={`segmented-btn${view === 'pull-requests' ? ' is-active' : ''}`}
              onClick={() => setView('pull-requests')}
            >
              Pull requests
            </button>
          </div>
        }
      />

      <section className="filters card">
        <div className="filter-row">
          <label style={{ flex: 1, minWidth: 200 }}>
            <span className="muted small">Search</span>
            <input
              className="input"
              placeholder="Search by PR title…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </label>
          {view === 'pull-requests' ? (
            <label>
              <span className="muted small">Status</span>
              <select className="select" value={status ?? ''} onChange={e => setStatus(e.target.value as AnalysisStatus | '')}>
                <option value="">All</option>
                {STATUSES.map(s => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label style={{ flex: 1, minWidth: 200 }}>
            <span className="muted small">Project</span>
            <MultiSelect
              options={repoOptions}
              selected={repository}
              onChange={values => updateMulti('repository', values)}
              placeholder="Type to filter projects…"
            />
          </label>
          <label style={{ flex: 1, minWidth: 200 }}>
            <span className="muted small">Developer</span>
            <MultiSelect
              options={authorOptions}
              selected={author}
              onChange={values => updateMulti('author', values)}
              placeholder="Type a github login…"
              trimLeadingChar="@"
            />
          </label>
          <label>
            <span className="muted small">Date range</span>
            <DateRangePicker from={fromDay} to={toDay} onChange={setDateRange} />
          </label>
        </div>
      </section>

      {view === 'work-items' ? (
        workItemsQuery.isLoading ? (
          <Spinner label="Loading work items…" />
        ) : workItemsQuery.error ? (
          <ErrorBanner error={workItemsQuery.error} onRetry={() => workItemsQuery.refetch()} />
        ) : (
          <WorkItemsView
            workItems={filterWorkItems(workItemsQuery.data ?? [], searchInput)}
            tableId="analyses-work-items"
            emptyHint="No completed work items match these filters."
          />
        )
      ) : null}

      {view === 'pull-requests' && isLoading ? <Spinner label="Loading pull requests…" /> : null}
      {view === 'pull-requests' && error ? <ErrorBanner error={error} onRetry={() => refetch()} /> : null}

      {view === 'pull-requests' && data && data.empty ? (
        <EmptyState title="No pull requests match" description="Adjust filters or wait for new PRs to be merged." />
      ) : null}

      {view === 'pull-requests' && data && !data.empty ? (
        <>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>PR</th>
                  <th>Project</th>
                  <th>Developer</th>
                  <th>Complexity</th>
                  <th>AI-assisted estimate</th>
                  <th>No-AI estimate</th>
                  <th>Merged</th>
                  {canDelete ? <th aria-label="Actions" /> : null}
                </tr>
              </thead>
              <tbody>
                {data.content.map(a => (
                  <tr key={a.id}>
                    <td>
                      <Link to={`/analyses/${a.id}`} className="link">
                        #{a.prNumber} {a.prTitle}
                      </Link>
                    </td>
                    <td>{repoDisplayName(a.repositoryFullName)}</td>
                    <td>@{a.prAuthor}</td>
                    <td>{a.complexity ?? '—'}</td>
                    <td>{formatEstimate(a.estimatedHoursMin, a.estimatedHoursLikely, a.estimatedHoursMax, mode)}</td>
                    <td>{formatEstimate(a.unassistedHoursMin, a.unassistedHoursLikely, a.unassistedHoursMax, mode)}</td>
                    <td className="muted small">{formatDateTime(a.createdAt)}</td>
                    {canDelete ? (
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-danger"
                          aria-label={`Delete analysis for PR #${a.prNumber}`}
                          title="Delete from history"
                          disabled={a.status === 'IN_PROGRESS' || removeMutation.isPending}
                          onClick={() => handleDelete(a.id, a.prNumber)}
                        >
                          🗑
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {removeMutation.error ? <ErrorBanner error={removeMutation.error} /> : null}

          <Pagination
            page={data.number}
            totalPages={data.totalPages || 1}
            onPrev={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            isFetching={isFetching}
          />
        </>
      ) : null}
    </>
  );
}

export function StatusBadge({ status }: { status: AnalysisStatus }) {
  const cls =
    status === 'COMPLETED' ? 'badge badge-good' :
    status === 'IN_PROGRESS' ? 'badge badge-info' :
    status === 'PENDING' ? 'badge badge-muted' :
    status === 'SKIPPED_BRANCH_EXCLUDED' ? 'badge badge-muted' :
    status === 'SKIPPED_NO_CREDITS' ? 'badge badge-amber' :
    'badge badge-warn';
  return <span className={cls}>{STATUS_LABELS[status] ?? status}</span>;
}
